/*
 * Fernzugang zum Servicezugang (docs/servicezugang-oem-und-fern.md, Teil 3).
 * Jede Aktion ist eine signierte manage.v1-Anfrage; die Ebene (Fachmann oder
 * OEM) setzt der Server aus den Rechten, der Client prüft sie. Ablauf wie in
 * der Geräteverwaltung: POST .../service/..., dann /api/manage/requests/{id}
 * nachfragen; Leseantworten kommen als Blob.
 */
import { api, ApiError } from '../api/client';

interface RequestState {
  correlationId: string;
  state: string;
  status: number | null;
  errorCode: string | null;
  errorMessage: string | null;
  body: unknown;
  contentType: string | null;
  blobSha256: string | null;
}

export class RemoteFailure extends Error {
  constructor(message: string, readonly status?: number) { super(message); }
}

const CODE_TEXT: Record<string, string> = {
  not_allowed: 'Das Gerät nimmt den Fernservice nicht an. Älterer Client?',
  busy: 'Das Gerät ist gerade beschäftigt. Gleich noch einmal versuchen.',
  timeout: 'Das Gerät hat nicht rechtzeitig geantwortet.',
  local_api_down: 'Die lokale Schnittstelle am Gerät antwortet nicht.',
};

/** Eine Anfrage an den Servicezugang und ihre Antwort. 422 ist bei
 *  schreiben eine Ablehnung des Reglers und kommt als Antwort zurück. */
export async function serviceCall<T>(plantId: string, clientId: string, path: string, body: unknown, waitSeconds = 45)
  : Promise<{ status: number; body: T; level: string }> {
  let sent: { correlationId: string; level: string };
  try {
    sent = await api.post<{ correlationId: string; level: string }>(
      `/api/plants/${plantId}/clients/${clientId}/service/${path}`, body ?? {});
  } catch (e) {
    if (e instanceof ApiError && e.status === 403) throw new RemoteFailure('Dafür fehlt dir das Recht service.fachmann oder service.oem.');
    if (e instanceof ApiError && e.status === 409) throw new RemoteFailure('Das Gerät hat noch keinen Verwaltungsschlüssel gemeldet.');
    if (e instanceof ApiError && e.status === 503) throw new RemoteFailure('Gerätefernverwaltung ist auf dieser Instanz nicht eingeschaltet.');
    throw new RemoteFailure('Anfrage konnte nicht abgesetzt werden.');
  }
  const until = Date.now() + waitSeconds * 1000;
  while (Date.now() < until) {
    await new Promise((r) => setTimeout(r, 600));
    const r = await api.get<RequestState>(`/api/manage/requests/${sent.correlationId}`);
    if (r.state === 'sent') continue;
    if (r.state !== 'answered') {
      throw new RemoteFailure(r.state === 'expired' ? 'Keine Antwort vom Gerät. Ist es online?' : 'Anfrage konnte nicht zugestellt werden.');
    }
    const status = r.status ?? 200;
    let payload: unknown = r.body;
    if ((payload === null || payload === undefined) && r.blobSha256 && (r.contentType ?? 'application/json').includes('json')) {
      const res = await fetch(`/api/manage/requests/${sent.correlationId}/blob`, { credentials: 'include' });
      if (!res.ok) throw new RemoteFailure(`Antwort des Geräts nicht abrufbar (HTTP ${res.status}).`);
      payload = await res.json();
    }
    if (status >= 400 && status !== 422) {
      const own = payload && typeof payload === 'object' ? (payload as { error?: string }).error : undefined;
      throw new RemoteFailure((r.errorCode && CODE_TEXT[r.errorCode]) || own || r.errorMessage || `Fehler ${status}`, status);
    }
    return { status, body: payload as T, level: sent.level };
  }
  throw new RemoteFailure(`Keine Antwort vom Gerät innerhalb von ${waitSeconds} s.`);
}

export function errorText(e: unknown): string {
  return e instanceof Error ? e.message : String(e);
}

/** Darf der Benutzer den OEM-Modus öffnen? */
export function canUseRemoteService(permissions: readonly string[] | undefined): boolean {
  return !!permissions && (permissions.includes('service.oem') || permissions.includes('service.fachmann'));
}

interface ClientList { clients: { runtimeInfo: { clientId: string } }[] }

/**
 * Field-Client, an dem das Gerät hängt: die Geräte-Id im Servicezugang ist
 * dieselbe wie in der Anlage (am TP10 nachgesehen, 22.10.2026). Bei mehreren
 * Clients einer Anlage wird jeder gefragt, bis einer das Gerät kennt.
 */
export async function findServiceClient(plantId: string, deviceId: string): Promise<{ clientId: string; level: string }> {
  const list = await api.get<ClientList>(`/api/clients?plantId=${encodeURIComponent(plantId)}`);
  const ids = (list.clients ?? []).map((c) => c.runtimeInfo.clientId);
  if (ids.length === 0) throw new RemoteFailure('Kein Field-Client an dieser Anlage.');
  let last: unknown = null;
  for (const clientId of ids) {
    try {
      const a = await serviceCall<{ id: string }[]>(plantId, clientId, 'geraete', {});
      if ((Array.isArray(a.body) ? a.body : []).some((d) => d.id === deviceId)) return { clientId, level: a.level };
    } catch (e) {
      last = e;
    }
  }
  if (last) throw last;
  throw new RemoteFailure('Kein Field-Client meldet dieses Gerät mit Servicezugang.');
}
